import React from "react";
import { Box, Typography } from "@mui/material";
import BarChartOutlined from "@mui/icons-material/BarChartOutlined";
import { parseFaqs, toInsightItems } from "../../../core/utils/analyzeInsights";

const GENERIC_MESSAGE_RE =
  /^(file\s+".+"\s+uploaded\.?|query results)$/i;

export const isGenericAnalyzeText = (text) =>
  GENERIC_MESSAGE_RE.test(String(text ?? "").trim());

export default function AnalyzeInsightsPanel({
  insights,
  faqs = [],
  fileName = "",
  darkMode,
  disabled = false,
  onSuggestedQuestion,
}) {
  const items = toInsightItems(insights);
  const questions = parseFaqs(faqs);

  if (items.length === 0 && questions.length === 0) {
    return null;
  }

  const titleColor = darkMode ? "#f8fafc" : "#0f172a";
  const bodyColor = darkMode ? "#d6deea" : "#475569";
  const cardBg = darkMode
    ? "rgba(255, 255, 255, 0.06)"
    : "rgba(15, 23, 42, 0.04)";
  const cardBorder = darkMode
    ? "rgba(255, 255, 255, 0.1)"
    : "rgba(15, 23, 42, 0.08)";
  const questionBg = darkMode ? "rgba(8, 14, 28, 0.72)" : "#eef2f7";
  const questionBorder = darkMode
    ? "rgba(255, 255, 255, 0.1)"
    : "rgba(15, 23, 42, 0.1)";
  const displayName = String(fileName || "").trim() || "unknown file";

  return (
    <Box sx={{ width: "100%", whiteSpace: "normal" }}>
      {items.length > 0 && (
        <Box
          sx={{
            p: 2.5,
            borderRadius: "18px",
            bgcolor: cardBg,
            border: `1px solid ${cardBorder}`,
          }}
        >
          <Typography
            sx={{ fontSize: 14, lineHeight: 1.6, color: titleColor, mb: 1.75 }}
          >
            I have processed file{" "}
            <Box component="span" sx={{ fontWeight: 700 }}>
              {displayName}
            </Box>
            . Successfully.
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              mb: 1.25,
              color: titleColor,
            }}
          >
            <BarChartOutlined sx={{ fontSize: 18, opacity: 0.9 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
              Initial Insights:
            </Typography>
          </Box>

          <Box
            component="ul"
            sx={{
              m: 0,
              pl: 2.5,
              display: "flex",
              flexDirection: "column",
              gap: 1.1,
            }}
          >
            {items.map((item, index) => (
              <Box
                component="li"
                key={`${item.title}-${index}`}
                sx={{
                  color: bodyColor,
                  fontSize: 14,
                  lineHeight: 1.65,
                }}
              >
                {item.title ? (
                  <>
                    <Box
                      component="span"
                      sx={{ fontWeight: 700, color: titleColor }}
                    >
                      {item.title}:
                    </Box>
                    {item.description ? ` ${item.description}` : null}
                  </>
                ) : (
                  item.description
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {questions.length > 0 && (
        <Box sx={{ mt: items.length ? 2.25 : 0 }}>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: darkMode ? "rgba(255,255,255,0.72)" : "#64748b",
              mb: 1.25,
            }}
          >
            Suggested questions
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 1.25,
            }}
          >
            {questions.map((question) => (
              <Box
                key={question}
                component="button"
                type="button"
                disabled={disabled || !onSuggestedQuestion}
                onClick={() => onSuggestedQuestion?.(question)}
                sx={{
                  textAlign: "left",
                  cursor: disabled ? "default" : "pointer",
                  color: titleColor,
                  fontSize: 13.5,
                  lineHeight: 1.5,
                  fontFamily: "inherit",
                  px: 2,
                  py: 1.75,
                  minHeight: 72,
                  borderRadius: "14px",
                  border: `1px solid ${questionBorder}`,
                  background: questionBg,
                  boxShadow: darkMode
                    ? "inset 0 1px 0 rgba(255,255,255,0.04)"
                    : "none",
                  transition: "background 160ms ease, border-color 160ms ease",
                  "&:hover:not(:disabled)": {
                    bgcolor: darkMode
                      ? "rgba(15, 23, 42, 0.92)"
                      : "rgba(15, 23, 42, 0.08)",
                    borderColor: darkMode
                      ? "rgba(255,255,255,0.22)"
                      : "rgba(15, 23, 42, 0.2)",
                  },
                  "&:disabled": {
                    opacity: 0.55,
                    cursor: "not-allowed",
                  },
                }}
              >
                {question}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
